import React from "react";
import { Panel, Loading } from "../components/panels";
import flatMap from "lodash/flatMap";
import { DraggableFreefield } from "./DraggableFreeField";
import { Modal } from "../components";
import AddFreefieldModal from "./AddFreefieldModal";
import { NoDataAvailableDiv } from "../components/NoDataAvailableDiv";
import { OrderableList } from "components/Tables";
import { ButtonSecondary } from "components/Atoms/Button";

interface Props {
  freeFields: server.dto.FreeFieldCategorySmall[];
  webform: server.dto.Webform;

}

const FreeFieldsInWebformPanel = (props: Props) => {
  const [selectedFreefields, setFreefields] = React.useState<server.dto.FreeFieldForWebform[]>(props.webform.freeFields);
  const [addModalOpen, setAddModalOpen] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const freefieldTypeMap = flatMap(props.freeFields.map(ffCat => ffCat.definitions), def => def)
    .reduce((acc, ff) => ({
      ...acc,
      [ff.freeFieldDefinitionId]: ff
    }), {});

  const onMandatoryToggle = async (id: number) => {
    const ffIdx = selectedFreefields.findIndex(ff => ff.freeFieldDefinitionId === id);
    const selectedFreeField = selectedFreefields[ffIdx];
    const isMandatory = !selectedFreeField.isMandatory;
    const newFFs = Object.assign([], selectedFreefields, { [ffIdx]: { ...selectedFreeField, isMandatory } });
    setFreefields(newFFs);

    await window.CoreApi.WebformConfiguration.StoreFreefield({
      webformId: props.webform.webformId,
      freeFieldDefinitionId: id,
      setting: isMandatory ? "visibleMandatory" : "visibleOptional"
    })
  };

  const onRemove = async (id: number) => {
    const res = await window.CoreApi.WebformConfiguration.DeleteFreefield({
      webformId: props.webform.webformId,
      freeFieldDefinitionId: id
    });
    if (!res.data.hasErrors) {
      const newFreefields = selectedFreefields.filter(ff => ff.freeFieldDefinitionId !== id);
      setFreefields(newFreefields);
    }
  };

  const onOrderChanged = async (rows) => {
    setFreefields(rows);
    await window.CoreApi.WebformConfiguration.SortFreefields({
      webformId: props.webform.webformId,
      freefieldDefinitionIds: rows.map(r => r.freeFieldDefinitionId),
    });
  };

  const onFreeFieldAdded = async (ff: server.dto.FreeFieldDefinitionSmall) => {
    setAddModalOpen(false);
    setIsLoading(true);
    const res = await window.CoreApi.WebformConfiguration.StoreFreefield({
      webformId: props.webform.webformId,
      freeFieldDefinitionId: ff.freeFieldDefinitionId,
      setting: "visibleOptional",
    });
    setIsLoading(false);
    if (!res.data.hasErrors) {
      setFreefields([...selectedFreefields, {
        freeFieldDefinitionId: ff.freeFieldDefinitionId,
        isMandatory: false,
      }]);
    }
  };

  return (
    <Panel
      panelClassName="s1_related-feature-panel"
      title={RESX.FreeField.AssignedFreeFields}
      button={<ButtonSecondary onClick={() => { setAddModalOpen(true); }} label={RESX.Buttons.btnAssign} />}
    >
      {isLoading && <Loading />}
      {(!isLoading && selectedFreefields.length === 0) && <NoDataAvailableDiv>{RESX.FreeField.NoFreeFieldsHaveBeenAssigned}</NoDataAvailableDiv>}
      {(!isLoading && selectedFreefields.length > 0) && <OrderableList
        items={selectedFreefields}
        getKey={ff => `${ff.freeFieldDefinitionId}`}
        render={(ff) => <DraggableFreefield
          definition={freefieldTypeMap[ff.freeFieldDefinitionId]}
          freefield={ff}
          onMandatoryToggle={onMandatoryToggle}
          onRemove={onRemove}
        />}
        onOrderChanged={onOrderChanged}
      />}

      <Modal
        title={RESX.Buttons.btnAssign}
        isOpen={addModalOpen}
        width="small"
        height={800}
        gap="bigger"
        handleClose={() => { setAddModalOpen(false); }}
      >
        <AddFreefieldModal
          categories={props.freeFields}
          onFreeFieldAdded={onFreeFieldAdded}
          excludeIds={selectedFreefields.map(ff => ff.freeFieldDefinitionId)}
        />
      </Modal>

    </Panel>
  );
};

export default FreeFieldsInWebformPanel;
